/**
 * Lot 4B — browser opt-in helpers for managing-superadmin Web Push.
 * Never prompts for Notification permission unless the user clicks Enable.
 * Explicit opt-in is stored locally and reconciled with server status — never
 * inferred from Notification.permission === "granted" alone.
 */

export const PROSPECT_PUSH_INSTALLATION_STORAGE_KEY = 'admin.prospectPush.installationId';
export const PROSPECT_PUSH_OPT_IN_STORAGE_KEY = 'admin.prospectPush.explicitOptIn';
export const PROSPECT_PUSH_PENDING_ENROLLMENT_STORAGE_KEY = 'admin.prospectPush.pendingEnrollment';

const INSTALLATION_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const CLIENT_OPERATION_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function createProspectPushInstallationId(randomUUID = globalThis.crypto?.randomUUID?.bind(globalThis.crypto)) {
  if (typeof randomUUID === 'function') {
    const id = randomUUID();
    if (INSTALLATION_ID_RE.test(id)) return id;
  }
  const bytes = new Uint8Array(16);
  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Read installation id without creating one (logout / cleanup). */
export function readProspectPushInstallationId(storage = globalThis.localStorage) {
  try {
    const existing = storage?.getItem?.(PROSPECT_PUSH_INSTALLATION_STORAGE_KEY);
    if (typeof existing === 'string' && INSTALLATION_ID_RE.test(existing)) return existing;
  } catch {
    /* ignore */
  }
  return null;
}

export function readOrCreateProspectPushInstallationId(storage = globalThis.localStorage) {
  const existing = readProspectPushInstallationId(storage);
  if (existing) return existing;
  const next = createProspectPushInstallationId();
  try {
    storage?.setItem?.(PROSPECT_PUSH_INSTALLATION_STORAGE_KEY, next);
  } catch {
    /* ignore */
  }
  return next;
}

/**
 * @param {string} uid
 * @returns {{ installationId: string, registrationGeneration: number | null, enrolledAt: string | null } | null}
 */
export function readExplicitPushOptIn(uid, storage = globalThis.localStorage) {
  if (typeof uid !== 'string' || !uid) return null;
  try {
    const raw = storage?.getItem?.(PROSPECT_PUSH_OPT_IN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.uid !== uid || parsed.optedIn !== true) return null;
    if (typeof parsed.installationId !== 'string' || !INSTALLATION_ID_RE.test(parsed.installationId)) {
      return null;
    }
    return {
      installationId: parsed.installationId,
      registrationGeneration: Number.isInteger(parsed.registrationGeneration)
        ? parsed.registrationGeneration
        : null,
      enrolledAt: typeof parsed.enrolledAt === 'string' ? parsed.enrolledAt : null,
    };
  } catch {
    return null;
  }
}

export function writeExplicitPushOptIn(
  {
    uid,
    installationId,
    registrationGeneration = null,
    enrolledAt = null,
  } = {},
  storage = globalThis.localStorage,
) {
  if (typeof uid !== 'string' || !uid) return false;
  if (typeof installationId !== 'string' || !INSTALLATION_ID_RE.test(installationId)) return false;
  try {
    storage?.setItem?.(
      PROSPECT_PUSH_OPT_IN_STORAGE_KEY,
      JSON.stringify({
        uid,
        installationId,
        registrationGeneration: Number.isInteger(registrationGeneration)
          ? registrationGeneration
          : null,
        enrolledAt: typeof enrolledAt === 'string' ? enrolledAt : null,
        optedIn: true,
      }),
    );
    return true;
  } catch {
    return false;
  }
}

export function clearExplicitPushOptIn(storage = globalThis.localStorage) {
  try {
    storage?.removeItem?.(PROSPECT_PUSH_OPT_IN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * @returns {{
 *   uid: string,
 *   installationId: string,
 *   clientOperationId: string,
 *   intent: string,
 *   startedAt: string | null,
 *   needsCompensation?: boolean,
 *   registrationGeneration?: number | null,
 *   enrolledAt?: string | null,
 * } | null}
 */
export function readPendingPushEnrollment(uid, storage = globalThis.localStorage) {
  if (typeof uid !== 'string' || !uid) return null;
  try {
    const raw = storage?.getItem?.(PROSPECT_PUSH_PENDING_ENROLLMENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.uid !== uid) return null;
    if (typeof parsed.installationId !== 'string' || !INSTALLATION_ID_RE.test(parsed.installationId)) {
      return null;
    }
    if (
      typeof parsed.clientOperationId !== 'string' ||
      !CLIENT_OPERATION_ID_RE.test(parsed.clientOperationId)
    ) {
      return null;
    }
    return {
      uid,
      installationId: parsed.installationId,
      clientOperationId: parsed.clientOperationId,
      intent: parsed.intent === 'enroll' ? 'enroll' : 'enroll',
      startedAt: typeof parsed.startedAt === 'string' ? parsed.startedAt : null,
      needsCompensation: parsed.needsCompensation === true,
      registrationGeneration: Number.isInteger(parsed.registrationGeneration)
        ? parsed.registrationGeneration
        : null,
      enrolledAt: typeof parsed.enrolledAt === 'string' ? parsed.enrolledAt : null,
    };
  } catch {
    return null;
  }
}

export function writePendingPushEnrollment(
  {
    uid,
    installationId,
    clientOperationId,
    intent = 'enroll',
    startedAt = null,
    needsCompensation = false,
    registrationGeneration = null,
    enrolledAt = null,
  } = {},
  storage = globalThis.localStorage,
) {
  if (typeof uid !== 'string' || !uid) return false;
  if (typeof installationId !== 'string' || !INSTALLATION_ID_RE.test(installationId)) return false;
  if (typeof clientOperationId !== 'string' || !CLIENT_OPERATION_ID_RE.test(clientOperationId)) {
    return false;
  }
  try {
    storage?.setItem?.(
      PROSPECT_PUSH_PENDING_ENROLLMENT_STORAGE_KEY,
      JSON.stringify({
        uid,
        installationId,
        clientOperationId,
        intent: intent === 'enroll' ? 'enroll' : 'enroll',
        startedAt: typeof startedAt === 'string' ? startedAt : null,
        needsCompensation: needsCompensation === true,
        registrationGeneration: Number.isInteger(registrationGeneration)
          ? registrationGeneration
          : null,
        enrolledAt: typeof enrolledAt === 'string' ? enrolledAt : null,
      }),
    );
    return true;
  } catch {
    return false;
  }
}

export function clearPendingPushEnrollment(storage = globalThis.localStorage) {
  try {
    storage?.removeItem?.(PROSPECT_PUSH_PENDING_ENROLLMENT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function isIosSafariWithoutHomeScreen({ userAgent, standalone } = {}) {
  const ua = typeof userAgent === 'string' ? userAgent : '';
  const isIos = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && /Mobile/i.test(ua));
  if (!isIos) return false;
  if (standalone === true) return false;
  return true;
}

export function readNotificationPermission(NotificationCtor = globalThis.Notification) {
  if (!NotificationCtor || typeof NotificationCtor.permission !== 'string') return 'unsupported';
  return NotificationCtor.permission;
}

/**
 * Whether a delayed async push result may still mutate UI / continue side effects.
 * Validates live session epoch as well as operation generation, UID, and installation.
 */
export function shouldApplyPushOperationResult({
  mounted,
  requestGeneration,
  activeGeneration,
  requestSessionEpoch,
  activeSessionEpoch,
  requestAuthUid,
  activeAuthUid,
  requestInstallationId,
  activeInstallationId,
  eligible = true,
} = {}) {
  if (!mounted) return false;
  if (eligible === false) return false;
  if (requestGeneration !== activeGeneration) return false;
  if (
    requestSessionEpoch != null &&
    activeSessionEpoch != null &&
    requestSessionEpoch !== activeSessionEpoch
  ) {
    return false;
  }
  if (String(requestAuthUid || '') !== String(activeAuthUid || '')) return false;
  if (
    requestInstallationId != null &&
    activeInstallationId != null &&
    String(requestInstallationId) !== String(activeInstallationId)
  ) {
    return false;
  }
  return true;
}

/**
 * Live ownership check used before server mutations (not only before setState).
 * Always reads the current session epoch from the provided getter.
 */
export function isProspectPushOperationLive({
  mounted,
  requestGeneration,
  getActiveGeneration,
  requestSessionEpoch,
  getSessionEpoch,
  requestAuthUid,
  getActiveAuthUid,
  requestInstallationId,
  getActiveInstallationId,
  eligible = true,
} = {}) {
  return shouldApplyPushOperationResult({
    mounted: typeof mounted === 'function' ? mounted() : mounted,
    requestGeneration,
    activeGeneration: typeof getActiveGeneration === 'function' ? getActiveGeneration() : null,
    requestSessionEpoch,
    activeSessionEpoch: typeof getSessionEpoch === 'function' ? getSessionEpoch() : null,
    requestAuthUid,
    activeAuthUid: typeof getActiveAuthUid === 'function' ? getActiveAuthUid() : null,
    requestInstallationId,
    activeInstallationId:
      typeof getActiveInstallationId === 'function' ? getActiveInstallationId() : null,
    eligible: typeof eligible === 'function' ? eligible() : eligible,
  });
}

/**
 * Derive UI state. Never treats permission=granted alone as consent.
 */
export function deriveProspectPushUiState({
  role,
  impersonating = false,
  permission,
  explicitOptIn = false,
  serverStatus = null,
  busy = false,
  busyKind = null,
  errorKey = null,
  iosHomeScreenHint = false,
  vapidConfigured = true,
  unknownOutcome = false,
} = {}) {
  if (role !== 'super_admin') return { kind: 'hidden' };
  if (impersonating) return { kind: 'impersonation_blocked' };

  // iOS home-screen guidance applies even when Notification is unavailable here.
  if (iosHomeScreenHint && permission !== 'granted') {
    return { kind: 'ios_home_screen' };
  }
  if (!vapidConfigured) return { kind: 'unavailable', reason: 'vapid_missing' };
  if (permission === 'unsupported') return { kind: 'unsupported' };
  if (permission === 'denied') return { kind: 'denied' };

  const serverActive = serverStatus === 'active';
  const enabled = explicitOptIn === true && serverActive === true && permission === 'granted';

  if (busy && busyKind === 'disable') return { kind: 'busy_disable' };
  if (busy) return { kind: 'busy' };

  if (errorKey === 'admin.notifications.push.disableError') {
    return { kind: 'disable_error', errorKey };
  }
  // Unknown enroll outcome keeps Enable retry — never infer consent from permission alone.
  if (unknownOutcome && !enabled) {
    return { kind: 'unknown_outcome', errorKey: errorKey || 'admin.notifications.push.error' };
  }
  if (errorKey) return { kind: 'error', errorKey };

  if (enabled) return { kind: 'enabled' };
  // Permission may be granted without explicit opt-in — still show Enable, never Disable.
  if (permission === 'granted' && !explicitOptIn) return { kind: 'granted_unregistered' };
  if (explicitOptIn && !serverActive) return { kind: 'opt_in_reconcile' };
  return { kind: 'opt_in' };
}
